import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { onboardingAPI } from '../../services/api';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { AssetSummary, REASON_TEXT, invalidateOverviewCache, progressColor } from '../../components/onboarding/onboardingTypes';

// All of the organisation's assets with their setup progress.
const OnboardingOverviewPage: React.FC = () => {
  useDocumentTitle('Setup checklist');
  const { currentOrganization } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [assets, setAssets] = useState<AssetSummary[] | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const welcome = params.get('welcome') === '1';

  const load = useCallback(async () => {
    try {
      const r = await onboardingAPI.overview(currentOrganization?.id);
      setAssets(r.data.data.assets || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not load your setup checklist');
      setAssets([]);
    }
  }, [currentOrganization?.id]);

  useEffect(() => { load(); }, [load]);

  const rerun = async (assetId: number) => {
    setBusyId(assetId);
    try {
      await onboardingAPI.restart(assetId);
      invalidateOverviewCache();
      navigate(`/onboarding/${assetId}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not restart setup');
    } finally {
      setBusyId(null);
    }
  };

  const pending = (assets || []).filter((a) => a.needs_onboarding);
  const hasUpgrade = pending.some((a) => a.reason === 'upgrade');

  return (
    <DashboardLayout noAssetRequired>
      <div className="container py-3" style={{ maxWidth: 960 }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <h1 className="h4 mb-1">Setup checklist</h1>
            <p className="text-muted mb-0 small">
              Make sure every asset is integrated properly. Steps turn green automatically as soon as we see real data.
            </p>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/dashboard')}>
            <i className="bi bi-house me-1"></i> Back to dashboard
          </button>
        </div>

        {(welcome || hasUpgrade) && pending.length > 0 && (
          <div className="alert border-0 d-flex gap-3 align-items-start" style={{ background: '#eef2ff', color: '#312e81' }}>
            <i className="bi bi-stars fs-4"></i>
            <div>
              <div className="fw-semibold">{REASON_TEXT.upgrade}</div>
              <div className="small">
                We've added a guided checklist that verifies your tracking script, analytics, uptime monitoring and notifications.
                Anything already working has been ticked off for you — it only takes a minute. You can leave and come back at any time.
              </div>
            </div>
          </div>
        )}

        {assets === null ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
        ) : assets.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="bi bi-box-seam fs-1 text-muted"></i>
              <p className="mt-2 mb-3">You don't have any assets yet.</p>
              <button className="btn btn-primary" onClick={() => navigate('/setup/asset')}>Add your first asset</button>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {assets.map((a) => {
              const pct = a.progress.percent;
              const done = !a.needs_onboarding;
              return (
                <div key={a.asset.id} className="card border-0 shadow-sm">
                  <div className="card-body d-flex align-items-center gap-3 flex-wrap">
                    <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="fw-semibold">{a.asset.name}</span>
                        {a.asset.platforms.website && <span className="badge bg-light text-dark border">Website</span>}
                        {a.asset.platforms.mobile_app && <span className="badge bg-light text-dark border">Mobile app</span>}
                        {done ? (
                          <span className="badge bg-success bg-opacity-10 text-success"><i className="bi bi-patch-check me-1"></i>Complete</span>
                        ) : a.reason === 'upgrade' ? (
                          <span className="badge bg-primary bg-opacity-10 text-primary">Re-check</span>
                        ) : a.reason === 'new' ? (
                          <span className="badge bg-warning bg-opacity-25 text-dark">New</span>
                        ) : (
                          <span className="badge bg-warning bg-opacity-25 text-dark">Needs attention</span>
                        )}
                        {a.state.snoozed && <span className="badge bg-light text-muted border">Snoozed</span>}
                      </div>
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <div className="progress flex-grow-1" style={{ height: 6, maxWidth: 360 }}>
                          <div className="progress-bar" style={{ width: `${pct}%`, background: progressColor(pct) }} />
                        </div>
                        <span className="small text-muted" style={{ minWidth: 120 }}>
                          {pct}% · required {a.progress.required_complete}/{a.progress.required_total}
                        </span>
                      </div>
                      {a.warnings.length > 0 ? (
                        <div className="small mt-1" style={{ color: '#b45309' }}>
                          <i className="bi bi-exclamation-triangle me-1"></i>{a.warnings[0].evidence}
                        </div>
                      ) : a.next_step && !done ? (
                        <div className="small text-muted mt-1">Next: {a.next_step.title}</div>
                      ) : null}
                    </div>
                    <div className="d-flex gap-2">
                      {done ? (
                        <>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(`/onboarding/${a.asset.id}`)}>View</button>
                          <button className="btn btn-sm btn-outline-primary" disabled={busyId === a.asset.id} onClick={() => rerun(a.asset.id)}>
                            <i className="bi bi-arrow-repeat me-1"></i>Re-run setup
                          </button>
                        </>
                      ) : (
                        <button className="btn btn-sm btn-primary" onClick={() => navigate(`/onboarding/${a.asset.id}`)}>
                          {a.state.started_at ? 'Continue' : 'Start'} setup <i className="bi bi-arrow-right ms-1"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OnboardingOverviewPage;
